import React, { useState } from "react";
import {
    MessageSquareText, MessageCircle, StickyNote, MoveUpRight, Slash, Square, Circle, Cloud, PenLine,
    Highlighter, Underline, Strikethrough, Eye, EyeOff, Plus, Ban, Type, ImagePlus, Bold, Italic, AlignLeft, AlignCenter, AlignRight,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Slider } from "../ui/slider";
import { FILL_TYPES, TEXT_TYPES, MARKUP_TYPES, fullStyle } from "../../lib/annotGeom";

export const COMMENT_TOOLS = [
    ["text", Type], ["comment", MessageSquareText], ["callout", MessageCircle], ["note", StickyNote], ["image", ImagePlus],
    ["arrow", MoveUpRight], ["line", Slash],
    ["rect", Square], ["ellipse", Circle], ["cloud", Cloud], ["draw", PenLine],
    ["highlight", Highlighter], ["underline", Underline], ["strike", Strikethrough],
];
const COLORS = ["#ef4444", "#f97316", "#facc15", "#22c55e", "#06b6d4", "#2563eb", "#a855f7", "#ec4899", "#ffffff", "#9ca3af", "#111827"];
const WIDTHS = [1, 2, 3, 5, 8];
const FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 40, 48, 64, 72];
const PRESETS = [
    ["note", { color: "#ca8a04", fill: "#fde047", fillOpacity: 0.4, textColor: "#111827" }],
    ["warning", { color: "#dc2626", fill: "#fecaca", fillOpacity: 0.5, textColor: "#991b1b" }],
    ["info", { color: "#2563eb", fill: "#dbeafe", fillOpacity: 0.6, textColor: "#1e3a8a" }],
    ["approved", { color: "#16a34a", fill: "#bbf7d0", fillOpacity: 0.5, textColor: "#14532d" }],
];
const seg = (on) => `h-6 min-w-6 px-1 flex items-center justify-center rounded text-xs transition-colors ${on ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-[#1e293b]"}`;

function ColorRow({ label, value, onPick, saved, onSave, allowNone, testId }) {
    const sw = (c) => (
        <button key={c} data-testid={`${testId}-${c.slice(1)}`} onClick={() => onPick(c)} title={c}
            className={`w-5 h-5 rounded-full border-2 transition-transform hover:scale-110 ${value === c ? "border-blue-400 ring-1 ring-white" : "border-white/20"}`}
            style={{ backgroundColor: c }} />
    );
    return (
        <div className="space-y-1">
            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</div>
            <div className="grid grid-cols-6 gap-1">
                {allowNone && (
                    <button data-testid={`${testId}-none`} onClick={() => onPick(null)} title="—"
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center text-rose-400 ${!value ? "border-blue-400" : "border-white/20"}`}>
                        <Ban className="w-3 h-3" />
                    </button>
                )}
                {COLORS.map(sw)}
                <label title="+" className="w-5 h-5 rounded-full border-2 border-dashed border-white/30 flex items-center justify-center cursor-pointer overflow-hidden relative">
                    <Plus className="w-3 h-3 text-slate-400" />
                    <input data-testid={`${testId}-custom`} type="color" value={value || "#000000"} onChange={(e) => onPick(e.target.value)}
                        className="absolute inset-0 opacity-0 cursor-pointer" />
                </label>
            </div>
            {saved.length > 0 && <div className="flex flex-wrap gap-1">{saved.map(sw)}</div>}
            {value && !saved.includes(value) && (
                <button data-testid={`${testId}-save`} onClick={() => onSave(value)} className="text-[10px] text-blue-300 hover:text-blue-200 underline">
                    + {value}
                </button>
            )}
        </div>
    );
}

// Vertical comment toolbar (left side of the PDF) with border/fill/text colors, fill opacity and line width.
export function CommentToolbar({ tool, setTool, styleFor, setStyle, selected, multi = [], cm, visible, setVisible, t }) {
    const { user, setUser } = useAuth();
    const saved = user?.saved_colors || [];
    const [pct, setPct] = useState(null);
    const target = selected && cm.canEdit(selected) ? selected : null;
    const targets = [target, ...multi.filter((c) => c.id !== selected?.id && cm.canEdit(c))].filter(Boolean);
    const type = target?.type || tool;
    const cur = target ? fullStyle(target) : type ? styleFor(type) : null;
    const apply = (patch) => {
        targets.forEach((c) => cm.update(c.id, { style: { ...fullStyle(c), ...patch } }));
        if (type) setStyle(type, patch);
    };
    const saveDefaultSize = async () => {
        try {
            const { data } = await api.put("/auth/me/prefs", { comment_font_size: cur.fontSize || 12 });
            setUser((u) => ({ ...u, comment_font_size: data.comment_font_size }));
            toast.success(t("cm_default_saved"));
        } catch { toast.error(t("cm_color_save_err")); }
    };
    const showText = targets.some((c) => TEXT_TYPES.includes(c.type)) || TEXT_TYPES.includes(type);
    const saveColor = async (c) => {
        const next = [...saved.filter((x) => x !== c), c].slice(-12);
        try {
            const { data } = await api.put("/auth/me/saved-colors", { colors: next });
            setUser((u) => ({ ...u, saved_colors: data.saved_colors }));
            toast.success(t("cm_color_saved"));
        } catch { toast.error(t("cm_color_save_err")); }
    };
    const btn = (active) => `h-8 w-8 flex items-center justify-center rounded-md transition-colors ${active ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-[#1e293b] hover:text-white"}`;
    const fo = Math.round((cur?.fillOpacity ?? 0) * 100);
    const hasFill = type && FILL_TYPES.includes(type);
    const opacityKey = hasFill ? "fillOpacity" : "strokeOpacity";
    const opVal = hasFill ? fo : Math.round((cur?.strokeOpacity ?? 1) * 100);
    const setOp = (v) => {
        const n = Math.max(0, Math.min(100, Math.round(Number(v) || 0)));
        const patch = { [opacityKey]: n / 100 };
        if (hasFill && n > 0 && !cur.fill) patch.fill = cur.color;
        apply(patch);
    };

    return (
        <div data-testid="comment-toolbar" onMouseDown={(e) => e.stopPropagation()} onWheel={(e) => e.stopPropagation()}
            className="absolute left-2 top-2 bottom-2 z-[44] flex gap-1.5 items-start pointer-events-none">
            <div className="pointer-events-auto flex flex-col gap-0.5 p-1 rounded-lg border border-[#1e293b] bg-[#0b0f17]/95 backdrop-blur-md shadow-xl">
                <button data-testid="comment-visibility-toggle" onClick={() => setVisible((v) => !v)} className={btn(false)}
                    title={visible ? t("cm_hide") : t("cm_show")}>
                    {visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4 text-slate-500" />}
                </button>
                <div className="h-px bg-[#1e293b] my-0.5" />
                {COMMENT_TOOLS.map(([k, Icon]) => (
                    <button key={k} data-testid={`comment-tool-${k}`} disabled={!visible}
                        onClick={() => setTool(tool === k ? null : k)} title={t(`cm_${k}`)}
                        className={`${btn(tool === k)} disabled:opacity-30`}>
                        <Icon className="w-4 h-4" />
                    </button>
                ))}
            </div>
            {cur && visible && (
                <div data-testid="comment-style-panel" className="pointer-events-auto w-[178px] max-h-full overflow-auto p-2 rounded-lg border border-[#1e293b] bg-[#0b0f17]/95 backdrop-blur-md shadow-xl space-y-2.5">
                    {targets.length > 1 && <div data-testid="comment-multi-count" className="text-[11px] font-mono text-amber-300">{t("cm_multi", { n: targets.length })}</div>}
                    {FILL_TYPES.includes(type) && (
                        <div className="space-y-1">
                            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{t("cm_presets")}</div>
                            <div className="grid grid-cols-2 gap-1">
                                {PRESETS.map(([k, p]) => (
                                    <button key={k} data-testid={`comment-preset-${k}`} onClick={() => apply(p)}
                                        className="text-[11px] px-1.5 py-1 rounded border transition-transform hover:scale-[1.03]"
                                        style={{ borderColor: p.color, backgroundColor: `${p.fill}66`, color: "#f8fafc" }}>{t(`cm_preset_${k}`)}</button>
                                ))}
                            </div>
                        </div>
                    )}
                    {showText && (
                        <div data-testid="comment-text-format" className="space-y-1">
                            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{t("cm_font")}</div>
                            <div className="flex items-center gap-1">
                                <select data-testid="comment-font-size" value={cur.fontSize || 12} onChange={(e) => apply({ fontSize: Number(e.target.value) })}
                                    className="flex-1 bg-[#111827] border border-[#1e293b] rounded text-xs px-1 py-0.5">
                                    {[...new Set([...FONT_SIZES, cur.fontSize || 12])].sort((a, b) => a - b).map((s) => <option key={s} value={s}>{s} pt</option>)}
                                </select>
                                <button data-testid="comment-font-default" onClick={saveDefaultSize} title={t("cm_default_size")}
                                    className="text-[10px] text-blue-300 hover:text-blue-200 underline whitespace-nowrap">{t("cm_default")}</button>
                            </div>
                            <div className="flex gap-0.5">
                                {[["sans", "Arial"], ["serif", "Times"], ["mono", "Courier"]].map(([f, l]) => (
                                    <button key={f} data-testid={`comment-font-${f}`} onClick={() => apply({ fontFamily: f })} className={`${seg(cur.fontFamily === f)} flex-1`}
                                        style={{ fontFamily: { sans: "Arial", serif: "Times New Roman", mono: "Courier New" }[f] }}>{l}</button>
                                ))}
                            </div>
                            <div className="flex gap-0.5">
                                {[["bold", Bold], ["italic", Italic], ["underline", Underline]].map(([f, Icon]) => (
                                    <button key={f} data-testid={`comment-fmt-${f}`} onClick={() => apply({ [f]: !cur[f] })} className={seg(cur[f])}><Icon className="w-3.5 h-3.5" /></button>
                                ))}
                                <span className="w-1.5" />
                                {[["left", AlignLeft], ["center", AlignCenter], ["right", AlignRight]].map(([a, Icon]) => (
                                    <button key={a} data-testid={`comment-align-${a}`} onClick={() => apply({ align: a })} className={seg(cur.align === a)}><Icon className="w-3.5 h-3.5" /></button>
                                ))}
                            </div>
                        </div>
                    )}
                    <ColorRow label={MARKUP_TYPES.includes(type) ? t("cm_color") : t("cm_border")} value={cur.color} onPick={(c) => apply({ color: c || cur.color })}
                        saved={saved} onSave={saveColor} testId="comment-color" />
                    {hasFill && (
                        <ColorRow label={t("cm_fill")} value={cur.fill && cur.fillOpacity > 0 ? cur.fill : null} allowNone
                            onPick={(c) => apply(c ? { fill: c, fillOpacity: cur.fillOpacity > 0 ? cur.fillOpacity : 0.4 } : { fillOpacity: 0 })}
                            saved={saved} onSave={saveColor} testId="comment-fill" />
                    )}
                    {!MARKUP_TYPES.includes(type) && (
                        <div className="space-y-1">
                            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{hasFill ? t("cm_fill_opacity") : t("cm_opacity")}</div>
                            <div className="flex items-center gap-2">
                                <Slider data-testid="comment-opacity-slider" value={[opVal]} min={0} max={100} step={10}
                                    onValueChange={(v) => setOp(v[0])} className="flex-1" />
                                <div className="flex items-center">
                                    <input data-testid="comment-opacity-input" type="number" min={0} max={100}
                                        value={pct ?? opVal} onChange={(e) => setPct(e.target.value)}
                                        onBlur={() => { if (pct !== null) setOp(pct); setPct(null); }}
                                        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                                        className="w-10 bg-[#111827] border border-[#1e293b] rounded text-xs text-right px-1 py-0.5 focus:outline-none" />
                                    <span className="text-xs text-slate-500 ml-0.5">%</span>
                                </div>
                            </div>
                        </div>
                    )}
                    {TEXT_TYPES.includes(type) && (
                        <ColorRow label={t("cm_text_color")} value={cur.textColor} onPick={(c) => apply({ textColor: c || cur.textColor })}
                            saved={saved} onSave={saveColor} testId="comment-text-color" />
                    )}
                    {!MARKUP_TYPES.includes(type) && type !== "note" && (
                        <div className="space-y-1">
                            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{t("cm_width")}</div>
                            <div className="flex gap-1">
                                {WIDTHS.map((w) => (
                                    <button key={w} data-testid={`comment-width-${w}`} onClick={() => apply({ width: w })}
                                        className={`w-6 h-6 rounded flex items-center justify-center ${cur.width === w ? "bg-blue-600" : "hover:bg-[#1e293b]"}`}>
                                        <span className="block rounded-full bg-slate-200" style={{ width: Math.min(14, w + 2), height: Math.min(14, w + 2) }} />
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
